import { useEffect, useState } from 'react'
import { Controller, useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FileText } from 'lucide-react'
import { DatePicker } from '@/components/ui/DatePicker'
import modalStyles from '@/features/application/styles/modals.module.css'
import cardStyles from '../styles/documentCard.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { TermsDocumentTemplateBlock } from './TermsDocumentTemplateBlock'
import { documentFormSchema, type DocumentFormValues } from '../types/schemas'
import type { ServiceAgreementDocument, ServiceAgreementEditFormProps, ServiceAgreementSubmitPayload } from '../types'
import { asTrimmedString } from '../utils/coerceStrings'
import { toDatePickerMinDate } from '../utils/effectiveDateRange'

export type { ServiceAgreementEditFormProps }

function normalizeDate(value: string | undefined): string {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function endOfYear(isoDate: string): string {
  if (!isoDate) return ''
  const match = isoDate.match(/^(\d{4})-/)
  if (!match) return ''
  return `${match[1]}-12-31`
}

function coerceBool(value: ServiceAgreementDocument['is_active'] | undefined): boolean {
  if (typeof value === 'boolean') return value
  return String(value) === '1' || String(value).toLowerCase() === 'true'
}


export function ServiceAgreementEditForm({
  document: doc,
  submitting,
  draftSaving,
  editorDirty,
  hasContentDraft,
  hasEorpDraft,
  onContentChange,
  onEorpChange,
  onSaveDraftContent,
  onSaveDraftEorp,
  onDiscardDraftContent,
  onDiscardDraftEorp,
  onSubmit,
  onCancel,
}: ServiceAgreementEditFormProps) {
  const [activeSection, setActiveSection] = useState<'section1' | 'section2'>('section1')

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema) as Resolver<DocumentFormValues>,
    mode: 'onChange',
    defaultValues: {
      title: '',
      version: '',
      content: '',
      eorp_section: '',
      effective_start_date: '',
      effective_end_date: '',
      is_active: false,
    },
  })

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch() is intentionally unmemoizable
  const contentValue = watch('content')
  const eorpValue = watch('eorp_section')
  const effectiveStartDate = watch('effective_start_date')
  const showActiveBadge = coerceBool(doc?.is_active)

  useEffect(() => {
    reset({
      title: doc?.title ?? '',
      version: doc?.version ?? '',
      content: asTrimmedString(doc?.content_draft ?? doc?.content),
      eorp_section: asTrimmedString(doc?.eorp_section_draft ?? doc?.eorp_section),
      effective_start_date: normalizeDate(doc?.effective_start_date),
      effective_end_date: normalizeDate(doc?.effective_end_date),
      is_active: coerceBool(doc?.is_active),
    })
  }, [doc, reset])

  const internalSubmit = (values: DocumentFormValues) => {
    const payload: ServiceAgreementSubmitPayload = {
      title: values.title.trim(),
      version: values.version.trim(),
      content: values.content,
      eorp_section: values.eorp_section ?? '',
      effective_start_date: values.effective_start_date,
      effective_end_date: values.effective_end_date,
      is_active: values.is_active,
    }
    onSubmit(payload, doc?.id, Boolean(doc?.id) && isDirty)
  }

  return (
    <form onSubmit={handleSubmit(internalSubmit)} className="w-full min-w-0">
      <div className="border-b border-slate-200 px-2 pb-0 sm:px-6">
        <nav
          className="grid min-w-0 grid-cols-2"
          aria-label="Service agreement sections"
          role="tablist"
        >
          <button
            type="button"
            onClick={() => setActiveSection('section1')}
            className={cn(
              'w-full border-b-2 px-2.5 py-3 text-center text-xs font-medium transition-colors sm:px-4 sm:py-4 sm:text-sm',
              activeSection === 'section1'
                ? 'border-[#1597ff] text-[#1597ff]'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
            )}
            role="tab"
            aria-selected={activeSection === 'section1'}
          >
            Section 1
          </button>
          <button
            type="button"
            onClick={() => setActiveSection('section2')}
            className={cn(
              'w-full border-b-2 px-2.5 py-3 text-center text-xs font-medium transition-colors sm:px-4 sm:py-4 sm:text-sm',
              activeSection === 'section2'
                ? 'border-[#1597ff] text-[#1597ff]'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
            )}
            role="tab"
            aria-selected={activeSection === 'section2'}
          >
            Section 2
          </button>
        </nav>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-5">
        {activeSection === 'section1' ? (
          <>
            <div className={cardStyles.card}>
              <div className={cardStyles.cardHeader}>
                <FileText className={cardStyles.cardIcon} />
                <h3 className={cardStyles.cardTitle}>Document Details</h3>
              </div>
              <div className={cardStyles.cardBody}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className={cardStyles.formGroup}>
                    <label
                      htmlFor="sa-title"
                      className={cardStyles.formLabel}
                    >
                      Document Name
                    </label>
                    <input
                      id="sa-title"
                      type="text"
                      className={cardStyles.customFormControl}
                      {...register('title')}
                    />
                    {errors.title ? (
                      <p className={modalStyles.formFieldError} role="alert">
                        {errors.title.message}
                      </p>
                    ) : null}
                  </div>
                  <div className={cardStyles.formGroup}>
                    <label
                      htmlFor="sa-version"
                      className={cardStyles.formLabel}
                    >
                      Version
                    </label>
                    <input
                      id="sa-version"
                      type="text"
                      className={cardStyles.customFormControl}
                      {...register('version')}
                    />
                    {errors.version ? (
                      <p className={modalStyles.formFieldError} role="alert">
                        {errors.version.message}
                      </p>
                    ) : null}
                  </div>
                  <div className={cardStyles.formGroup}>
                    <label
                      htmlFor="sa-start"
                      className={cardStyles.formLabel}
                    >
                      Effective Start Date
                    </label>
                    <Controller
                      name="effective_start_date"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          id="sa-start"
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value)
                            const currentEnd = watch('effective_end_date')
                            if (value && !currentEnd) {
                              setValue('effective_end_date', endOfYear(value), {
                                shouldValidate: true,
                                shouldDirty: true,
                              })
                            }
                          }}
                          onBlur={field.onBlur}
                          placeholder="MM/DD/YYYY"
                          allowFutureDates={true}
                          showHelperText={false}
                          className={cardStyles.customFormControl}
                          dataTestId="sa-effective-start-date"
                        />
                      )}
                    />
                    {errors.effective_start_date ? (
                      <p className={modalStyles.formFieldError} role="alert">
                        {errors.effective_start_date.message}
                      </p>
                    ) : null}
                  </div>
                  <div className={cardStyles.formGroup}>
                    <label
                      htmlFor="sa-end"
                      className={cardStyles.formLabel}
                    >
                      Effective End Date
                    </label>
                    <Controller
                      name="effective_end_date"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          id="sa-end"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="MM/DD/YYYY"
                          allowFutureDates={true}
                          showHelperText={false}
                          className={cardStyles.customFormControl}
                          dataTestId="sa-effective-end-date"
                          minDate={toDatePickerMinDate(effectiveStartDate)}
                        />
                      )}
                    />
                    {errors.effective_end_date ? (
                      <p className={modalStyles.formFieldError} role="alert">
                        {errors.effective_end_date.message}
                      </p>
                    ) : null}
                  </div>
                  {showActiveBadge ? (
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input type="checkbox" className="rounded" checked disabled />
                        Active
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <TermsDocumentTemplateBlock
              heading="Document Content"
              editorId="sa-content"
              value={contentValue || ''}
              onChange={(value) => {
                setValue('content', value, { shouldValidate: true, shouldDirty: true })
                onContentChange(value)
              }}
              onSaveDraft={onSaveDraftContent}
              onDiscardDraft={onDiscardDraftContent}
              saveDraftDisabled={!editorDirty.content}
              discardDraftDisabled={!hasContentDraft}
              draftSaving={draftSaving}
              error={errors.content?.message}
            />
          </>
        ) : (
          <TermsDocumentTemplateBlock
            heading="EORP Content"
            editorId="sa-eorp-section"
            value={eorpValue || ''}
            onChange={(value) => {
              setValue('eorp_section', value, { shouldDirty: true })
              onEorpChange(value)
            }}
            onSaveDraft={onSaveDraftEorp}
            onDiscardDraft={onDiscardDraftEorp}
            saveDraftDisabled={!editorDirty.eorp}
            discardDraftDisabled={!hasEorpDraft}
            draftSaving={draftSaving}
          />
        )}
      </div>

      <div className="border-t border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={(Boolean(doc?.id) && !isDirty) || submitting}
            className={cn(primaryEntityActionButtonClass, 'px-4 py-2')}
          >
            {submitting ? 'Saving…' : 'Submit'}
          </button>
          <button type="button" onClick={onCancel} className={cn(secondaryEntityActionButtonClass, 'px-4 py-2')}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  )
}
